import React from "react";
import locale from "locale";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import clone from "lodash.clonedeep";
import {
    Row,
    Col,
    DropdownButton,
    MenuItem,
    ControlLabel,
    FormControl,
    ButtonGroup,
    Button,
    Alert,
    ButtonToolbar,
} from "react-bootstrap";

import {
    getBlocks,
    getGlobalSettings,
    formDataBuilder,
    createTemplate,
    templateDetails,
    previewTemplate as fetchPreviewTemplate,
    previewBlock as fetchPreviewBlock,
    templateActions,
    editTemplate,
} from "../../../../../api/cp";
import Breadcrumbs from "../../../UI/Breadcrumbs";
import Tooltip from "../../../UI/Tooltip";
import LetterTemplatePreview from "../../../UI/HtmlPreview";
import {
    INITIAL_TEMPLATE_MODEL,
    MAX_BANNER_DIMENSIONS,
    MAX_BANNER_SIZE,
} from "../models";

import LetterTemplateBlock from "./LetterTemplateBlock";
import styles from "./styles.module.scss";
import {ucFirst} from "../../../../../admin/js/lib";

const globalPlaceholders = [
    "eventName",
    "timeStart",
    "timeStop",
    "dateStart",
    "dateStop",
    "datetimeStart",
    "datetimeStop",
    "userEmail",
];

const LetterTemplate = ({ event }) => {
    const { templateId } = useParams();
    const history = useHistory();

    const posterUploadRef = React.useRef(null);

    const [isCreateTemplate, setIsCreateTemplate] = React.useState(true);
    const [blocks, setBlocks] = React.useState([]);
    const [previewTemplate, setPreviewTemplate] = React.useState(null);
    const [model, setModel] = React.useState(clone(INITIAL_TEMPLATE_MODEL));
    const [alertMessage, setAlertMessage] = React.useState("");
    const [template, setTemplate] = React.useState(null);

    const activeCrumbs = [
        "mainMenu.emails",
        "mainMenu.emails.lettersConstructor",
        `mainMenu.emails.${
            isCreateTemplate ? "createLetterTemplate" : "editLetterTemplate"
        }`,
    ];

    React.useEffect(async () => {
        setIsCreateTemplate(!templateId);
        templateId && handleTemplate(templateId);
    }, [templateId]);

    React.useEffect(async () => {
        const blocks = await getBlocks(event.eventKey);
        blocks && setBlocks(blocks);

        if (!templateId) {
            const settings = await getGlobalSettings(event.eventPassword);
            settings?.posterUrl &&
                updateModelField({
                    field: "banner",
                    value: settings?.posterUrl,
                });
        }
    }, []);

    React.useEffect(() => {
        const timer = setTimeout(() => setAlertMessage(null), 3000);

        return () => clearTimeout(timer);
    }, [alertMessage]);

    const isBlocksEmpty = () => blocks.length === 0;
    const isBannerExist = () => model.banner !== null;
    const isBannerTypeFile = () => model.banner instanceof File;
    const isPreviewTemplateExist = () => Boolean(previewTemplate);

    function reorder(list, startIndex, endIndex) {
        const result = clone(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    }

    function onDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const orderedBlocks = reorder(
            model.blocks,
            result.source.index,
            result.destination.index
        );

        updateModelField({ field: "blocks", value: orderedBlocks });
    }

    function handleSelectBlock(block) {
        updateModelField({
            field: "blocks",
            value: [...new Set([...model.blocks, block])],
        });

        const filtredBlocks = blocks.filter(({ id }) => id !== block.id);

        setBlocks(filtredBlocks);
    }

    function onBlockDelete(block) {
        const filtredBlocks = model.blocks.filter(({ id }) => id !== block.id);
        updateModelField({ field: "blocks", value: filtredBlocks });
        setBlocks((prev) => [
            ...new Set([...prev, block].sort((a, b) => a.id - b.id)),
        ]);
    }

    function updateModelField({ field, value }) {
        const newModel = {
            ...model,
            [field]: value,
        };

        setModel(newModel);
    }

    async function handlePosterSelect(file) {
        const { valid, message } = await validateBanner(file);

        if (!valid) {
            return setAlertMessage(message);
        }

        updateModelField({ field: "banner", value: file });
    }

    function handlePosterUpload() {
        posterUploadRef.current && posterUploadRef.current.click();
    }

    function handlePosterEdit() {
        handlePosterUpload();
    }

    async function handlePosterDelete() {
        if (templateId) {
            await templateActions(templateId, "deleteBanner");
        }

        updateModelField({
            field: "banner",
            value: null,
        });
    }

    function getImageSrcFromFile(file) {
        try {
            return URL.createObjectURL(file);
        } catch (error) {
            return null;
        }
    }

    async function validateBanner(banner) {
        if (!banner) {
            return { message: locale("email.posterNotFound"), valid: false };
        }

        if (banner.size > MAX_BANNER_SIZE) {
            return {
                message: locale("email.maximumFileSizeExceeded"),
                valid: false,
            };
        }

        const { width, height } = await imageSize(banner);

        if (
            MAX_BANNER_DIMENSIONS.WIDTH < width ||
            MAX_BANNER_DIMENSIONS.HEIGHT < height
        ) {
            return {
                message: locale("email.maximumFileResolutionExceeded"),
                valid: false,
            };
        }

        return { valid: true };
    }

    async function imageSize(file) {
        const image = new Image();

        const promise = new Promise((resolve, reject) => {
            image.onload = () => {
                const [width, height] = [
                    image.naturalWidth,
                    image.naturalHeight,
                ];
                resolve({ width, height });
            };

            image.onerror = reject;
        });

        image.src = getImageSrcFromFile(file);

        return promise;
    }

    function handleBack() {
        history.push(`/event/${event.eventPassword}/emailSettings/constructor`);
    }

    async function handleSaveTemplate() {
        const payload = {
            ...model,
            blocks: model.blocks.map(({ id }) => id),
            banner: isBannerTypeFile() ? model.banner : null,
        };

        const errorMessage = validatePayload(payload);

        if (errorMessage) {
            alert(errorMessage);

            return;
        }

        const formData = formDataBuilder(payload);
        const response = await createTemplate(event.eventKey, formData);

        response && handleBack();
    }

    async function onBlockPreview(block) {
        const preview = await fetchPreviewBlock(block.id);
        setPreviewTemplate(preview);
    }

    async function handlePreviewTemplate() {
        const preview = await fetchPreviewTemplate(template.id);
        setPreviewTemplate(preview);
    }

    async function handleTemplate(templateId) {
        const template = await templateDetails(templateId);
        setTemplate(template);

        const newModel = Object.entries(template).reduce(
            (acc, [key, value]) => {
                if (Object.prototype.hasOwnProperty.call(model, key)) {
                    acc[key] = value;
                }

                return acc;
            },
            {}
        );

        setModel(newModel);
    }

    async function handleEditTemplate() {
        const payload = {
            ...model,
            blocks: model.blocks.map(({ id }) => id),
        };

        const errorMessage = validatePayload(payload);

        if (errorMessage) {
            alert(errorMessage);

            return;
        }

        const formData = formDataBuilder(payload);
        const response = await editTemplate(templateId, formData);

        response && handleBack();
    }

    function validatePayload(payload) {
        if (!payload.blocks?.length) {
            return locale("email.selectAtLeastOneBlock");
        }

        if (!payload.name?.trim()) {
            return locale("email.setTemplateName");
        }

        if (!(payload.themeRu?.trim() && payload.themeEn?.trim())) {
            return locale("email.setMailSubject");
        }

        return null;
    }

    const placeholders = [
        ...globalPlaceholders,
        'userName',
        event.registrationFields.map(({field}) => 'user' + ucFirst(field.name)),
    ]

    return (
        <section className={styles.template}>
            <Breadcrumbs
                link={`/event/${event.eventPassword}`}
                activeCrumbs={activeCrumbs}
            />

            {alertMessage && <Alert bsStyle="danger">{alertMessage}</Alert>}

            <Row style={{ marginBottom: "15px" }}>
                <Col xs={4}>
                    <ControlLabel>{locale("email.templateName")}</ControlLabel>
                    <FormControl
                        type="text"
                        value={model.name}
                        onChange={({ target }) =>
                            updateModelField({
                                field: "name",
                                value: target.value,
                            })
                        }
                        placeholder={locale("email.templateName")}
                    />
                </Col>
            </Row>

            <Row style={{ marginBottom: "15px" }}>
                <Col xs={4}>
                    <ControlLabel>
                        {locale("email.templateDescription")}
                    </ControlLabel>
                    <FormControl
                        componentClass="textarea"
                        value={model.description}
                        onChange={({ target }) =>
                            updateModelField({
                                field: "description",
                                value: target.value,
                            })
                        }
                        placeholder={locale("email.templateDescription")}
                    />
                </Col>
            </Row>

            <Row style={{ marginBottom: "15px" }}>
                <Col xs={4}>
                    <ControlLabel>
                        {locale("email.subjectLine")} (ru)
                    </ControlLabel>
                    <FormControl
                        type="text"
                        value={model.themeRu}
                        onChange={({ target }) =>
                            updateModelField({
                                field: "themeRu",
                                value: target.value,
                            })
                        }
                        placeholder={`${locale("email.subjectLine")} (ru)`}
                    />
                </Col>

                <Col xs={4}>
                    <ControlLabel>
                        {locale("email.subjectLine")} (en)
                    </ControlLabel>
                    <FormControl
                        type="text"
                        value={model.themeEn}
                        onChange={({ target }) =>
                            updateModelField({
                                field: "themeEn",
                                value: target.value,
                            })
                        }
                        placeholder={`${locale("email.subjectLine")} (en)`}
                    />
                </Col>

                <Col xs={12}>
                    <p>
                        <i>
                            {locale("email.availableValues")}:{" "}
                            {placeholders
                                .map((name) => `{{${name}}}`)
                                .join(", ")}
                        </i>
                    </p>
                </Col>
            </Row>

            <Row style={{ marginBottom: "15px" }}>
                <Col xs={4}>
                    <ControlLabel>
                        {locale("email.poster")}
                        <Tooltip width="400px">
                            {locale("email.templatePosterTooltip")}
                        </Tooltip>
                    </ControlLabel>
                    <div>
                        <FormControl
                            type="file"
                            inputRef={(ref) => (posterUploadRef.current = ref)}
                            style={{ display: "none" }}
                            onChange={({ target }) =>
                                handlePosterSelect(target.files[0])
                            }
                        />
                        {isBannerExist() && (
                            <img
                                src={
                                    isBannerTypeFile()
                                        ? getImageSrcFromFile(model.banner)
                                        : model.banner
                                }
                                alt={locale("email.poster")}
                                className={styles.banner}
                            />
                        )}
                        <ButtonGroup className={styles.buttons}>
                            {isBannerExist() ? (
                                <>
                                    <Button onClick={handlePosterEdit}>
                                        {locale("email.edit")}
                                    </Button>
                                    <Button onClick={handlePosterDelete}>
                                        {locale("email.delete")}
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={handlePosterUpload}>
                                    {locale("email.upload")}
                                </Button>
                            )}
                        </ButtonGroup>
                    </div>
                </Col>
            </Row>

            <ControlLabel>{locale("email.blocks")}</ControlLabel>
            <Row style={{ marginBottom: "15px" }}>
                <Col lg={12}>
                    <DropdownButton
                        id="blocks-dropdown"
                        title={locale(
                            isBlocksEmpty()
                                ? "email.blockListIsEmpty"
                                : "email.selectBlock"
                        )}
                        noCaret={isBlocksEmpty()}
                        onSelect={handleSelectBlock}
                        disabled={isBlocksEmpty()}
                    >
                        {blocks.map((block) => (
                            <MenuItem
                                key={`dropdown-${block.id}-${Math.random()}`}
                                eventKey={block}
                            >
                                {block.name}
                            </MenuItem>
                        ))}
                    </DropdownButton>
                </Col>
            </Row>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {model.blocks.map((block, index) => (
                                <Draggable
                                    key={block.id}
                                    draggableId={`${block.id}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={
                                                provided.draggableProps.style
                                            }
                                        >
                                            <LetterTemplateBlock
                                                block={block}
                                                onPreview={onBlockPreview}
                                                onDelete={onBlockDelete}
                                                className={styles.block}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <Row>
                <Col xs={4}>
                    <ButtonToolbar className={styles.buttons}>
                        {isCreateTemplate ? (
                            <Button
                                bsStyle="success"
                                onClick={handleSaveTemplate}
                            >
                                {locale("email.save")}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    bsStyle="info"
                                    onClick={handlePreviewTemplate}
                                >
                                    {locale("email.watch")}
                                </Button>
                                <Button
                                    bsStyle="success"
                                    onClick={handleEditTemplate}
                                >
                                    {locale("email.edit")}
                                </Button>
                            </>
                        )}
                        <Button onClick={handleBack}>
                            {locale("email.back")}
                        </Button>
                    </ButtonToolbar>
                </Col>
            </Row>

            <LetterTemplatePreview
                isModalVisible={isPreviewTemplateExist()}
                template={previewTemplate}
                onHide={() => setPreviewTemplate(null)}
            />
        </section>
    );
};

const mapStateToProps = (store) => {
    return {
        event: store.currentEvent,
    };
};

export default connect(mapStateToProps)(LetterTemplate);
